import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, MapPin, ShieldCheck, Clock } from "lucide-react";
import { SITE, scrollToSection } from "@/lib/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1574757987642-5755f0839101?crop=entropy&cs=srgb&fm=jpg&q=85";

const LINES = ["HEAVY-DUTY", "CRAFTSMANSHIP &", "RELIABLE CONTRACTING"];

const BADGES = [
  { icon: MapPin, text: "LAWRENCEVILLE, IL & 45-MILE RADIUS" },
  { icon: Clock, text: "EMERGENCY SUPPORT AVAILABLE" },
  { icon: ShieldCheck, text: "FULLY LICENSED & INSURED" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[15%] h-[130%]">
        <img src={HERO_IMG} alt="Construction crew framing a structure" className="h-full w-full object-cover" />
      </motion.div>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to right, rgba(12,14,18,0.94) 0%, rgba(12,14,18,0.72) 55%, rgba(12,14,18,0.35) 100%)",
        }}
      />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0c0e12] to-transparent" />

      <motion.div style={{ opacity: contentOpacity }} className="relative z-10 px-6 lg:px-12 max-w-[1400px] mx-auto w-full pt-32 pb-20">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-mono text-xs sm:text-sm uppercase tracking-[0.4em] text-orange-500 mb-6"
          data-testid="hero-eyebrow"
        >
          {SITE.tagline}
        </motion.p>

        <h1 className="font-display uppercase leading-[0.88] text-white text-[clamp(3.2rem,9.5vw,8.5rem)]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
              >
                {i === 1 ? (
                  <>
                    CRAFTSMANSHIP <span className="text-orange-500">&</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Licensed HVAC, Plumbing, Carpentry & Handyman experts serving Lawrenceville, IL and the
          surrounding 45-mile service radius.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-estimate"
            onClick={() => scrollToSection("#contact")}
            className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-[#0c0e12] font-mono text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 transition-all duration-200 hover:-translate-y-0.5"
          >
            Request Free Estimate
            <ArrowRight size={16} strokeWidth={2.5} />
          </button>
          <a
            href={SITE.phoneHref}
            data-testid="hero-cta-call"
            className="inline-flex items-center gap-3 border border-white/25 hover:border-orange-500 text-white hover:text-orange-500 font-mono text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 transition-all duration-200 hover:-translate-y-0.5"
          >
            <Phone size={16} strokeWidth={2.5} />
            {SITE.phoneDisplay}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
          data-testid="hero-badges"
        >
          {BADGES.map(({ icon: Icon, text }) => (
            <span key={text} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
              <Icon size={14} className="text-orange-500" />
              {text}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <div className="hidden xl:block absolute right-10 bottom-24 z-10 [writing-mode:vertical-rl] font-mono text-[10px] uppercase tracking-[0.5em] text-slate-500">
        EST. LAWRENCEVILLE, ILLINOIS
      </div>
    </section>
  );
};
